import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { type AdDetail, adKey } from "../api/ads";
import { ApiError, api } from "../api/client";
import { useMe } from "../api/me";
import { type NewModel, useCreateModel, useModels } from "../api/models";
import type { ModelEngine } from "../api/types";
import { creditsWord } from "../copy/errors";
import { SectionHeader } from "../assets/SectionHeader";
import { ErrorLine } from "../ui/ErrorLine";
import { UploadButton } from "../library/UploadButton";
import { Button, Field, Input, PageHeader, Skeleton } from "../ui";
import { STUDIO_EYEBROW } from "./DesignListPage";
import { StudioThreadBanner } from "./StudioThreadBanner";
import { estimateLine } from "./format";

export const PROMPT_MAX = 1000;
export const COUNTER_FROM = 900;
export const PHOTOS_MAX = 6;
export const PHOTOS_TITLE = "Up to 6 photos";
export const SIZE_ERROR = "Enter a size between 0.5 and 5,000 cm.";
export const MOST_OBJECTS = "Most objects take two to four instructions.";
export const ENGINE_LEGEND = "How should it be made?";
export const ENGINES: { id: ModelEngine; label: string; helper: string }[] = [
  {
    id: "agent",
    label: "Built",
    helper: "We write it part by part; best for products, packaging, furniture",
  },
  {
    id: "mesh",
    label: "Sculpted",
    helper: "Generated in one piece; best for animals, characters, plants",
  },
];
export const SCULPTED_CHARGE =
  "Each generation is charged when it produces a model, whether or not you keep it. A photo of the real thing improves the result.";
/** "Sculpted designs are generated by Meshy; your description and photos are
 *  sent to them." */
export function providerLine(provider: string): string {
  return `Sculpted designs are generated by ${provider}; your description and photos are sent to them.`;
}
/** `?describe=` is cut to this many characters. */
const DESCRIBE_MAX = 200;

/** The request the server would refuse for too few credits, so that the
 *  page's own line and the server's read the same ([ux §5.4]). */
export function shortfall(available: number, needed: number): ApiError {
  return new ApiError(402, "insufficient_credits", "", { available, needed });
}

/** Centimetres as typed, to metres; null when empty, NaN when not allowed. */
export function metresOf(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0.5 || value > 5000) return NaN;
  return Math.round(value * 1e4) / 1e6;
}

interface Photo {
  id: string;
  name: string;
  url: string;
}

/** `/assets/studio/new`: describe an object, and it is modelled
 *  ([design-studio §11.3], [ux §8.16]). With a provider of sculpted designs
 *  set, the user first chooses how: built part by part, or sculpted in one
 *  piece for a flat fee that is said before the click ([sculpted §5.1]). */
export function NewDesignPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const models = useModels();
  const me = useMe();
  const create = useCreateModel();

  // Read once, on arrival; the address then loses the parameter.
  const [prompt, setPrompt] = useState(() =>
    (search.get("describe") ?? "").trim().slice(0, DESCRIBE_MAX),
  );
  // Opened from an ad's cast panel (docs/SPEC.md §6.4): the ad and its row
  // stay in the address; the design is made for them.
  const [thread] = useState(() => {
    const adId = search.get("ad");
    const item = search.get("item");
    return adId && item ? { ad_id: adId, item, thing: prompt } : null;
  });
  const threadAd = useQuery({
    queryKey: adKey(thread?.ad_id ?? ""),
    queryFn: () =>
      api<AdDetail>(`/v1/ads/${encodeURIComponent(thread?.ad_id ?? "")}`),
    enabled: !!thread,
    retry: false,
  });
  useEffect(() => {
    if (!search.has("describe")) return;
    const next = new URLSearchParams(search);
    next.delete("describe");
    setSearch(next, { replace: true });
  }, [search, setSearch]);

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [size, setSize] = useState("");
  const [engine, setEngine] = useState<ModelEngine>("agent");

  const head = models.data?.pages[0];
  const estimate = head?.turn_estimate;
  const available = me.data?.balance.available ?? null;
  const loading = models.isPending || me.isPending;
  // The choice is offered only while a provider is set ([sculpted §5.1]).
  const offered = me.data?.features?.sculpted === true;
  const sculpted = offered && engine === "mesh";
  // What must be available: the price of a generation, or the estimate of a
  // design's first instruction (§15.1). A balance that is not known makes no
  // check.
  const needed = sculpted ? head?.mesh_fee : estimate?.first.credits;
  const short =
    needed !== undefined && available !== null && available < needed;
  const metres = metresOf(size);
  const sizeWrong = Number.isNaN(metres);
  const text = prompt.trim();
  const context = sculpted ? "generate" : "instruction";

  const shortError = useMemo(
    () =>
      short && needed !== undefined && available !== null
        ? shortfall(available, needed)
        : null,
    [short, needed, available],
  );

  const disabled =
    loading ||
    !estimate ||
    short ||
    sizeWrong ||
    text.length === 0 ||
    create.isPending;

  const onCreate = () => {
    if (disabled) return;
    const body: NewModel = { prompt: text };
    if (photos.length > 0) body.refs = photos.map((photo) => photo.id);
    if (metres !== null) body.real_size_m = metres;
    if (sculpted) body.engine = "mesh";
    if (thread && threadAd.data) {
      body.for_ad = { ad_id: thread.ad_id, item: thread.item };
    }
    create.mutate(body, {
      onSuccess: (reply) =>
        navigate(`/assets/studio/${encodeURIComponent(reply.id)}`),
    });
  };

  return (
    <>
      <PageHeader eyebrow={STUDIO_EYEBROW} title="New design" />
      <SectionHeader />
      <StudioThreadBanner
        adId={thread?.ad_id}
        adTitle={threadAd.data?.title}
        thing={thread?.thing}
      />
      <form
        className="flex max-w-2xl flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          onCreate();
        }}
      >
        {offered ? (
          <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0">
            <legend className="tb-field-label mb-2 p-0">{ENGINE_LEGEND}</legend>
            <div
              role="radiogroup"
              aria-label={ENGINE_LEGEND}
              className="grid gap-3 sm:grid-cols-2"
            >
              {ENGINES.map((option) => {
                const checked = option.id === engine;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    onClick={() => setEngine(option.id)}
                    className={[
                      "min-h-[44px] rounded-lg border p-4 text-left transition-colors",
                      checked
                        ? "border-brand bg-brand-tint/60 shadow-glow"
                        : "border-slate-200 bg-white hover:border-brand-light",
                    ].join(" ")}
                  >
                    <span className="block font-semibold text-slate-900">
                      {option.label}
                    </span>
                    <span className="block text-xs text-slate-600">
                      {option.helper}
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>
        ) : null}

        <Field
          label="Describe the object"
          helper={
            prompt.length >= COUNTER_FROM ? (
              <span data-testid="prompt-counter">
                {prompt.length.toLocaleString("en-US")} /{" "}
                {PROMPT_MAX.toLocaleString("en-US")}
              </span>
            ) : undefined
          }
        >
          <textarea
            className="tb-input min-h-[120px] resize-y"
            value={prompt}
            maxLength={PROMPT_MAX}
            placeholder="A stainless steel water bottle with a loop on the cap"
            onChange={(event) => setPrompt(event.target.value)}
          />
        </Field>

        <div className="flex flex-col gap-2" data-testid="photos">
          <div className="tb-field-label">
            Photos of the real thing (optional, up to 6)
          </div>
          {photos.length > 0 ? (
            <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
              {photos.map((photo) => (
                <li key={photo.id} className="relative">
                  <img
                    src={photo.url}
                    alt={photo.name}
                    className="block h-20 w-20 rounded-lg bg-slate-100 object-cover"
                  />
                  <button
                    type="button"
                    aria-label={`Remove ${photo.name}`}
                    className="absolute -right-2 -top-2 flex h-11 w-11 items-center justify-center rounded-full text-base leading-none sm:h-7 sm:w-7"
                    onClick={() =>
                      setPhotos((now) => now.filter((p) => p.id !== photo.id))
                    }
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-slate-700 shadow-card"
                    >
                      ×
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="flex flex-wrap items-center gap-2 max-sm:[&_.tb-btn]:min-h-[44px]">
            <UploadButton
              label="Upload an image"
              size="sm"
              testId="photo-input"
              disabled={photos.length >= PHOTOS_MAX}
              title={photos.length >= PHOTOS_MAX ? PHOTOS_TITLE : undefined}
              onUploaded={(id, file) =>
                setPhotos((now) =>
                  now.length >= PHOTOS_MAX
                    ? now
                    : [
                        ...now,
                        { id, name: file.name, url: URL.createObjectURL(file) },
                      ],
                )
              }
            />
          </div>
        </div>

        <Field
          label="Longest side (cm, optional)"
          error={sizeWrong ? SIZE_ERROR : undefined}
          className="max-w-[240px]"
        >
          <Input
            type="number"
            inputMode="decimal"
            min={0.5}
            max={5000}
            step="any"
            value={size}
            invalid={sizeWrong}
            onChange={(event) => setSize(event.target.value)}
          />
        </Field>

        <div className="flex flex-col gap-1" data-testid="estimate">
          {loading ? (
            <Skeleton width={420} height={18} data-testid="estimate-skeleton" />
          ) : models.isError || !estimate ? (
            <ErrorLine error={models.error} context="design" />
          ) : sculpted && head ? (
            <>
              <p
                className="m-0 text-sm font-semibold text-slate-800"
                data-testid="mesh-fee"
              >
                {creditsWord(head.mesh_fee)}
              </p>
              {shortError ? (
                <ErrorLine error={shortError} context="generate" />
              ) : null}
            </>
          ) : shortError ? (
            <ErrorLine error={shortError} context="instruction" />
          ) : (
            <p
              className="m-0 text-sm text-slate-700"
              data-testid="estimate-line"
            >
              {estimateLine(estimate.first, estimate.hold)}
            </p>
          )}
          {sculpted && head ? (
            <>
              {head.mesh_provider ? (
                <p
                  className="m-0 text-sm text-slate-600"
                  data-testid="mesh-provider"
                >
                  {providerLine(head.mesh_provider)}
                </p>
              ) : null}
              <p className="m-0 text-sm text-slate-600">{SCULPTED_CHARGE}</p>
            </>
          ) : (
            <p className="m-0 text-sm text-slate-600">{MOST_OBJECTS}</p>
          )}
        </div>

        <div className="flex flex-col items-start gap-2">
          <Button
            type="submit"
            variant="primary"
            className="min-h-[44px] w-full disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:w-auto"
            disabled={disabled}
            aria-busy={create.isPending ? true : undefined}
          >
            {create.isPending ? "Creating…" : "Create"}
          </Button>
          {create.isError ? (
            <ErrorLine error={create.error} context={context} />
          ) : null}
        </div>
      </form>
    </>
  );
}
